// src/components/EditProjectModal.tsx
import React, { useState, useEffect, useRef } from 'react';
import { X, Search, UserPlus, Crown, Trash2, Users, Shield, ChevronRight, ChevronLeft, Lock, Pencil, FolderOpen, Tags, UserCheck, Save } from 'lucide-react';
import { getCookie } from '../utils/utils';
import { useCaps } from '../contexts/CapabilitiesContext';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  profile_picture?: string;
  is_catalyx_user?: boolean;
}

interface TeamOption {
  id: number;
  name: string;
  is_default: boolean;
}

interface ProjectMember {
  id: number;
  user: number;
  user_email: string;
  user_name: string;
  role: string;
  joined_at: string;
}

interface Project {
  id: number;
  name: string;
  goal?: string;
  category?: string;
  markets?: string[];
  territory_demand_space?: string;
  occasion?: string;
  score?: number;
  owner?: number;
  owner_email?: string;
  members?: ProjectMember[];
  is_owner?: boolean;
  member_count?: number;
  created_at?: string;
  team?: number | null;
  team_name?: string | null;
  [key: string]: any;
}

interface EditProjectModalProps {
  isOpen: boolean;
  project: Project | null;
  onClose: () => void;
  onProjectUpdated: (project: any) => void;
}

/* ------------------------------------------------------------------ */
/*  Extracted sub-components (stable references)                       */
/* ------------------------------------------------------------------ */

const STEP_COLORS = [
  { bg: 'bg-[#C3D74F]', bgLight: 'bg-[#C3D74F]/15', text: 'text-[#8B9A2B]', shadow: 'shadow-[#C3D74F]/30', bar: '#C3D74F' },
  { bg: 'bg-[#43C8F5]', bgLight: 'bg-[#43C8F5]/15', text: 'text-[#2A9CC4]', shadow: 'shadow-[#43C8F5]/30', bar: '#43C8F5' },
  { bg: 'bg-[#807CB1]', bgLight: 'bg-[#807CB1]/15', text: 'text-[#5D5A8C]', shadow: 'shadow-[#807CB1]/30', bar: '#807CB1' },
];

const StepperBar: React.FC<{ currentPage: number; totalPages: number; onStepClick?: (step: number) => void }> = ({ currentPage, totalPages, onStepClick }) => {
  const steps = [
    { num: 1, label: 'Name', icon: FolderOpen },
    { num: 2, label: 'Details', icon: Tags },
    { num: 3, label: 'Team', icon: UserCheck },
  ];

  return (
    <div className="flex items-center justify-between mb-8 px-2">
      {steps.slice(0, totalPages).map((step, idx) => {
        const Icon = step.icon;
        const isActive = step.num === currentPage;
        const isComplete = step.num < currentPage;
        const color = STEP_COLORS[idx];
        return (
          <React.Fragment key={step.num}>
            {idx > 0 && (
              <div className="flex-1 mx-2 h-0.5 rounded-full overflow-hidden bg-gray-200">
                <div
                  className="h-full rounded-full transition-all duration-500 ease-out"
                  style={{
                    width: isComplete || isActive ? '100%' : '0%',
                    backgroundColor: isComplete ? STEP_COLORS[idx - 1].bar : isActive ? color.bar : undefined,
                  }}
                />
              </div>
            )}
            <button
              onClick={() => onStepClick?.(step.num)}
              className="flex flex-col items-center gap-1.5 focus:outline-none group"
              disabled={!onStepClick}
            >
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${
                  isActive
                    ? `${color.bg} text-white shadow-lg ${color.shadow} scale-110`
                    : isComplete
                    ? `${color.bgLight} ${color.text} ${onStepClick ? 'group-hover:scale-105 cursor-pointer' : ''}`
                    : `bg-gray-100 text-gray-400 ${onStepClick ? 'group-hover:bg-gray-200 cursor-pointer' : ''}`
                }`}
              >
                {isComplete ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <Icon className="w-4 h-4" />
                )}
              </div>
              <span
                className={`text-[11px] font-medium transition-colors duration-300 ${
                  isActive ? color.text : isComplete ? color.text : 'text-gray-400'
                } ${onStepClick ? 'group-hover:text-gray-600' : ''}`}
              >
                {step.label}
              </span>
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
};

const ReadOnlyField: React.FC<{ label: string; value: string | string[] | undefined }> = ({ label, value }) => {
  const displayValue = Array.isArray(value) ? (value.length > 0 ? value.join(', ') : '-') : (value || '-');
  return (
    <div>
      <label className="block text-sm font-medium text-gray-500 mb-1.5">{label}</label>
      <div className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700">
        {Array.isArray(value) && value.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {value.map((v) => (
              <span
                key={v}
                className="inline-flex items-center bg-[#C3D74F]/15 text-[#8B9A2B] text-xs px-2.5 py-0.5 rounded-full border border-[#C3D74F]/30"
              >
                {v}
              </span>
            ))}
          </div>
        ) : (
          displayValue
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

const TOTAL_PAGES = 3;

const EditProjectModal: React.FC<EditProjectModalProps> = ({
  isOpen,
  project,
  onClose,
  onProjectUpdated,
}) => {
  const { can } = useCaps();
  const canEditProject = can('projects_edit');
  const [currentPage, setCurrentPage] = useState(1);
  const [animDirection, setAnimDirection] = useState<'forward' | 'backward'>('forward');

  // Page 1: Name
  const [name, setName] = useState('');

  // Page 3: Assignments
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [currentMembers, setCurrentMembers] = useState<ProjectMember[]>([]);
  const [pendingAdditions, setPendingAdditions] = useState<User[]>([]);
  const [pendingRemovals, setPendingRemovals] = useState<number[]>([]);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const [catalyxSearchQuery, setCatalyxSearchQuery] = useState('');
  const [catalyxSearchResults, setCatalyxSearchResults] = useState<User[]>([]);
  const [selectedCatalyxUsers, setSelectedCatalyxUsers] = useState<User[]>([]);
  const [isCatalyxSearching, setIsCatalyxSearching] = useState(false);
  const [showCatalyxDropdown, setShowCatalyxDropdown] = useState(false);
  const catalyxSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const catalyxDropdownRef = useRef<HTMLDivElement>(null);
  const [currentUserData, setCurrentUserData] = useState<{ is_catalyx_user?: boolean; org?: number } | null>(null);

  // Populate form when project changes
  useEffect(() => {
    if (project) {
      setCurrentPage(1);
      setAnimDirection('forward');
      setName(project.name || '');
      setCurrentMembers(project.members || []);
      setPendingAdditions([]);
      setPendingRemovals([]);
      setSearchQuery('');
      setError(null);
      setSelectedTeamId(project.team ?? null);
      setSelectedCatalyxUsers([]);
      setCatalyxSearchQuery('');
      fetchTeams();
      fetchCurrentUserData();
    }
  }, [project]);

  const fetchTeams = async () => {
    try {
      const response = await fetch('/api/teams/?mine=1', { credentials: 'include' });
      if (response.ok) setTeams(await response.json());
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  };

  const fetchCurrentUserData = async () => {
    try {
      const response = await fetch('/api/user/profile/', { credentials: 'include' });
      if (response.ok) setCurrentUserData(await response.json());
    } catch (err) {
      console.error('Error fetching user data:', err);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) setShowDropdown(false);
      if (catalyxDropdownRef.current && !catalyxDropdownRef.current.contains(event.target as Node)) setShowCatalyxDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Catalyx support search
  useEffect(() => {
    if (catalyxSearchTimeoutRef.current) clearTimeout(catalyxSearchTimeoutRef.current);
    if (catalyxSearchQuery.length < 2) { setCatalyxSearchResults([]); setShowCatalyxDropdown(false); return; }
    catalyxSearchTimeoutRef.current = setTimeout(async () => {
      setIsCatalyxSearching(true);
      try {
        const orgId = currentUserData?.org;
        const response = await fetch(`/api/users/search-catalyx-support/?q=${encodeURIComponent(catalyxSearchQuery)}&org_id=${orgId || ''}`, { credentials: 'include' });
        if (response.ok) {
          const users = await response.json();
          const existingIds = new Set([...currentMembers.map(m => m.user), ...selectedCatalyxUsers.map(u => u.id)]);
          const filtered = users.filter((u: User) => !existingIds.has(u.id));
          setCatalyxSearchResults(filtered);
          setShowCatalyxDropdown(filtered.length > 0);
        }
      } catch (err) { console.error('Error searching catalyx users:', err); }
      finally { setIsCatalyxSearching(false); }
    }, 300);
    return () => { if (catalyxSearchTimeoutRef.current) clearTimeout(catalyxSearchTimeoutRef.current); };
  }, [catalyxSearchQuery, selectedCatalyxUsers, currentMembers, currentUserData?.org]);

  // Search users with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (searchQuery.length < 2) { setSearchResults([]); setShowDropdown(false); return; }
    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await fetch(`/api/users/search/?q=${encodeURIComponent(searchQuery)}`, { credentials: 'include' });
        if (response.ok) {
          const users = await response.json();
          const existingUserIds = new Set([...currentMembers.map(m => m.user), ...pendingAdditions.map(u => u.id)]);
          const filteredUsers = users.filter((user: User) => !existingUserIds.has(user.id) || pendingRemovals.includes(user.id));
          setSearchResults(filteredUsers);
          setShowDropdown(filteredUsers.length > 0);
        }
      } catch (err) { console.error('Error searching users:', err); }
      finally { setIsSearching(false); }
    }, 300);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchQuery, currentMembers, pendingAdditions, pendingRemovals]);

  const handleAddUser = (user: User) => {
    if (pendingRemovals.includes(user.id)) {
      setPendingRemovals(prev => prev.filter(id => id !== user.id));
    } else {
      setPendingAdditions(prev => [...prev, user]);
    }
    setSearchQuery(''); setSearchResults([]); setShowDropdown(false);
  };

  const handleRemoveExistingMember = (userId: number) => {
    if (project && project.owner && userId === project.owner) return;
    setPendingRemovals(prev => [...prev, userId]);
  };

  const getUserDisplayName = (user: User | { user_name?: string; user_email?: string }) => {
    if ('user_name' in user && user.user_name) return user.user_name;
    if ('first_name' in user && 'last_name' in user) {
      const fullName = `${user.first_name} ${user.last_name}`.trim();
      return fullName || user.email;
    }
    return (user as any).user_email || '';
  };

  const handleNext = () => {
    if (currentPage === 1 && !name.trim()) {
      setError('Project name is required.');
      return;
    }
    setError(null);
    setAnimDirection('forward');
    setCurrentPage(prev => Math.min(prev + 1, TOTAL_PAGES));
  };

  const handleBack = () => {
    setAnimDirection('backward');
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!project) return;
    if (!canEditProject) {
      setError('You do not have permission to edit projects.');
      return;
    }
    if (!name.trim()) { setError('Project name is required.'); setCurrentPage(1); return; }

    setError(null);
    setIsLoading(true);
    const csrftoken = getCookie('csrftoken');

    try {
      const response = await fetch(`/api/projects/${project.id}/`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrftoken || '' },
        body: JSON.stringify({
          name,
          team: selectedTeamId || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.error || 'Failed to update project');
      }

      for (const userId of pendingRemovals) {
        await fetch(`/api/projects/${project.id}/remove_member/`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrftoken || '' },
          body: JSON.stringify({ user_id: userId }),
        });
      }

      for (const user of pendingAdditions) {
        await fetch(`/api/projects/${project.id}/add_member/`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrftoken || '' },
          body: JSON.stringify({ user_id: user.id, role: 'member' }),
        });
      }

      for (const user of selectedCatalyxUsers) {
        await fetch(`/api/projects/${project.id}/add_member/`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrftoken || '' },
          body: JSON.stringify({ user_id: user.id, role: 'member' }),
        });
      }

      const updatedProjectResponse = await fetch(`/api/projects/${project.id}/`, { credentials: 'include' });
      if (updatedProjectResponse.ok) {
        onProjectUpdated(await updatedProjectResponse.json());
      } else {
        onProjectUpdated(await response.json());
      }
      onClose();
    } catch (err: any) {
      setError(err.message || 'An error occurred while updating the project');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !project) return null;
  if (!canEditProject) return null;

  const isOwner = project.is_owner ?? false;
  const displayMembers = currentMembers.filter(m => !pendingRemovals.includes(m.user));
  const removedMembers = currentMembers.filter(m => pendingRemovals.includes(m.user));

  const pageSubtitles: Record<number, string> = {
    1: 'Update your project name.',
    2: 'These details are locked after creation.',
    3: 'Manage team members and access.',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 animate-in fade-in zoom-in-95 slide-in-from-bottom-4 duration-300">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
          {/* Header */}
            <div className="h-1 bg-gradient-to-r from-[#C3D74F] via-[#43C8F5] to-[#807CB1]" />
          <div className="bg-white px-6 pt-5 pb-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Pencil className="w-5 h-5 text-[#C3D74F]" />
                  <h2 className="text-lg font-bold text-gray-900">Edit Project</h2>
                </div>
                <p className="text-gray-500 text-sm">{pageSubtitles[currentPage]}</p>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 pt-6 pb-4 max-h-[60vh] overflow-y-auto">
            <StepperBar currentPage={currentPage} totalPages={TOTAL_PAGES} onStepClick={setCurrentPage} />

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl animate-in fade-in slide-in-from-top-2 duration-200">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Animated page wrapper */}
            <div
              key={currentPage}
              className={`animate-in fade-in duration-300 ${
                animDirection === 'forward' ? 'slide-in-from-right-4' : 'slide-in-from-left-4'
              }`}
            >
              {/* ========== PAGE 1: Name (editable) ========== */}
              {currentPage === 1 && (
                <div className="space-y-4">
                  <div>
                    <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Project Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="edit-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white text-gray-900 placeholder-gray-400
                        focus:outline-none focus:ring-2 focus:ring-[#43C8F5]/40 focus:border-[#43C8F5]
                        transition-all duration-200 hover:border-gray-300 text-base"
                      placeholder="Enter project name"
                      disabled={isLoading}
                      autoFocus
                    />
                    <p className="mt-2 text-xs text-gray-400">
                      Update the name of your project.
                    </p>
                  </div>
                </div>
              )}

              {/* ========== PAGE 2: Details (read-only) ========== */}
              {currentPage === 2 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <p className="text-xs text-amber-700">
                      Project details are locked after creation. Contact an administrator if changes are needed.
                    </p>
                  </div>

                  <ReadOnlyField label="Market(s)" value={project.markets} />
                  <ReadOnlyField label="Territory / Demand Space" value={project.territory_demand_space} />
                  <ReadOnlyField label="Category" value={project.category} />
                  <ReadOnlyField label="Occasion" value={project.occasion} />
                </div>
              )}

              {/* ========== PAGE 3: Assignments (editable) ========== */}
              {currentPage === 3 && (
                <div className="space-y-5">
                  {/* Assign Catalyx Support (non-catalyx users only) */}
                  {currentUserData && !currentUserData.is_catalyx_user && isOwner && (
                    <div>
                      <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                        <Shield className="w-4 h-4 text-[#807CB1]" />
                        Assign Catalyx Support
                      </label>
                      <div className="relative" ref={catalyxDropdownRef}>
                        <div className="relative">
                          <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            value={catalyxSearchQuery}
                            onChange={(e) => setCatalyxSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl bg-white placeholder-gray-400
                              focus:outline-none focus:ring-2 focus:ring-[#43C8F5]/40 focus:border-[#43C8F5]
                              transition-all duration-200 hover:border-gray-300"
                            placeholder="Search Catalyx support users..."
                            disabled={isLoading}
                          />
                          {isCatalyxSearching && (
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#43C8F5]/30 border-t-[#43C8F5]"></div>
                            </div>
                          )}
                        </div>
                        {showCatalyxDropdown && catalyxSearchResults.length > 0 && (
                          <div className="absolute z-20 w-full mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl max-h-48 overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-150">
                            {catalyxSearchResults.map((user) => (
                              <button
                                key={user.id}
                                type="button"
                                onClick={() => {
                                  setSelectedCatalyxUsers(prev => [...prev, user]);
                                  setCatalyxSearchQuery('');
                                  setCatalyxSearchResults([]);
                                  setShowCatalyxDropdown(false);
                                }}
                                className="w-full px-4 py-2.5 text-left hover:bg-gray-50 flex items-center gap-3 border-b border-gray-50 last:border-b-0 transition-colors first:rounded-t-xl last:rounded-b-xl"
                              >
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#807CB1] to-[#5D5A8C] flex items-center justify-center flex-shrink-0">
                                  <span className="text-white text-sm font-medium">{(user.first_name?.[0] || user.email[0]).toUpperCase()}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{getUserDisplayName(user)}</p>
                                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {selectedCatalyxUsers.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          <p className="w-full text-xs text-green-600">{selectedCatalyxUsers.length} Catalyx support user(s) to add</p>
                          {selectedCatalyxUsers.map((user) => (
                            <div
                              key={user.id}
                              className="flex items-center gap-2 bg-[#807CB1]/10 border border-[#807CB1]/25 rounded-full pl-1 pr-2 py-1 animate-in fade-in zoom-in-90 duration-200"
                            >
                              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#807CB1] to-[#5D5A8C] flex items-center justify-center">
                                <span className="text-white text-xs font-medium">{(user.first_name?.[0] || user.email[0]).toUpperCase()}</span>
                              </div>
                              <span className="text-sm text-[#5D5A8C] truncate max-w-[120px]">{getUserDisplayName(user)}</span>
                              <button
                                type="button"
                                onClick={() => setSelectedCatalyxUsers(prev => prev.filter(u => u.id !== user.id))}
                                className="text-[#807CB1]/50 hover:text-[#807CB1] transition-colors"
                                disabled={isLoading}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Team Members */}
                  <div>
                    <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                      <UserPlus className="w-4 h-4 text-[#43C8F5]" />
                      Team Members
                    </label>

                    {/* Current Members */}
                    {displayMembers.length > 0 && (
                      <div className="mb-3 space-y-2">
                        <p className="text-xs text-gray-500">{displayMembers.length} current member{displayMembers.length !== 1 ? 's' : ''}</p>
                        <div className="space-y-1.5">
                          {displayMembers.map((member) => {
                            const isOwnerMember = project.owner ? member.user === project.owner : false;
                            return (
                              <div key={member.id} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-xl transition-colors hover:bg-[#43C8F5]/5">
                                <div className="flex items-center gap-2.5">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#43C8F5] to-[#2A9CC4] flex items-center justify-center flex-shrink-0">
                                    <span className="text-white text-sm font-medium">{(member.user_name?.[0] || member.user_email[0]).toUpperCase()}</span>
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                                      {member.user_name || member.user_email}
                                      {isOwnerMember && <span title="Owner"><Crown className="w-3 h-3 text-yellow-500" /></span>}
                                    </p>
                                    <p className="text-xs text-gray-500">{member.user_email}</p>
                                  </div>
                                </div>
                                {isOwner && !isOwnerMember && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveExistingMember(member.user)}
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all duration-200"
                                    disabled={isLoading}
                                    title="Remove member"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Members marked for removal */}
                    {removedMembers.length > 0 && (
                      <div className="mb-3 space-y-2">
                        <p className="text-xs text-red-500">{removedMembers.length} member{removedMembers.length !== 1 ? 's' : ''} will be removed</p>
                        <div className="space-y-1.5">
                          {removedMembers.map((member) => (
                            <div key={member.id} className="flex items-center justify-between p-2.5 bg-red-50 border border-red-200 rounded-xl opacity-60">
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-full bg-red-200 flex items-center justify-center flex-shrink-0">
                                  <span className="text-red-600 text-sm font-medium">{(member.user_name?.[0] || member.user_email[0]).toUpperCase()}</span>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-500 line-through">{member.user_name || member.user_email}</p>
                                  <p className="text-xs text-gray-400">{member.user_email}</p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => setPendingRemovals(prev => prev.filter(id => id !== member.user))}
                                className="text-xs text-gray-700 hover:text-gray-900 font-medium px-2 py-1 rounded-lg hover:bg-gray-100 transition-all duration-200"
                                disabled={isLoading}
                              >
                                Undo
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Pending Additions */}
                    {pendingAdditions.length > 0 && (
                      <div className="mb-3 space-y-2">
                        <p className="text-xs text-green-600">{pendingAdditions.length} new member{pendingAdditions.length !== 1 ? 's' : ''} to add</p>
                        <div className="flex flex-wrap gap-2">
                          {pendingAdditions.map((user) => (
                            <div
                              key={user.id}
                              className="flex items-center gap-2 bg-[#43C8F5]/10 border border-[#43C8F5]/25 rounded-full pl-1 pr-2 py-1 animate-in fade-in zoom-in-90 duration-200"
                            >
                              {user.profile_picture ? (
                                <img src={user.profile_picture} alt={getUserDisplayName(user)} className="w-6 h-6 rounded-full object-cover" />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#43C8F5] to-[#2A9CC4] flex items-center justify-center">
                                  <span className="text-white text-xs font-medium">{(user.first_name?.[0] || user.email[0]).toUpperCase()}</span>
                                </div>
                              )}
                              <span className="text-sm text-[#2A9CC4] truncate max-w-[120px]">{getUserDisplayName(user)}</span>
                              <button
                                type="button"
                                onClick={() => setPendingAdditions(prev => prev.filter(u => u.id !== user.id))}
                                className="text-[#43C8F5]/50 hover:text-[#43C8F5] transition-colors"
                                disabled={isLoading}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* User Search - Only for owners */}
                    {isOwner && (
                      <div className="relative" ref={dropdownRef}>
                        <div className="relative">
                          <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl bg-white placeholder-gray-400
                              focus:outline-none focus:ring-2 focus:ring-[#43C8F5]/40 focus:border-[#43C8F5]
                              transition-all duration-200 hover:border-gray-300"
                            placeholder="Search users to add..."
                            disabled={isLoading}
                          />
                          {isSearching && (
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#43C8F5]/30 border-t-[#43C8F5]"></div>
                            </div>
                          )}
                        </div>
                        {showDropdown && searchResults.length > 0 && (
                          <div className="absolute z-20 w-full mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl max-h-48 overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-150">
                            {searchResults.map((user) => (
                              <button
                                key={user.id}
                                type="button"
                                onClick={() => handleAddUser(user)}
                                className="w-full px-4 py-2.5 text-left hover:bg-gray-50 flex items-center gap-3 border-b border-gray-50 last:border-b-0 transition-colors first:rounded-t-xl last:rounded-b-xl"
                              >
                                {user.profile_picture ? (
                                  <img src={user.profile_picture} alt={getUserDisplayName(user)} className="w-8 h-8 rounded-full object-cover" />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#43C8F5] to-[#2A9CC4] flex items-center justify-center flex-shrink-0">
                                    <span className="text-white text-sm font-medium">{(user.first_name?.[0] || user.email[0]).toUpperCase()}</span>
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{getUserDisplayName(user)}</p>
                                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {!isOwner && (
                      <p className="text-xs text-gray-400 mt-2">Only the project owner can manage team members.</p>
                    )}
                  </div>

                  {/* Assign Team */}
                  {teams.length > 0 && isOwner && (
                    <div>
                      <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                        <Users className="w-4 h-4 text-[#43C8F5]" />
                        Assign Team
                      </label>
                      <select
                        value={selectedTeamId ?? ''}
                        onChange={(e) => setSelectedTeamId(e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-white text-gray-900
                          focus:outline-none focus:ring-2 focus:ring-[#43C8F5]/40 focus:border-[#43C8F5]
                          transition-all duration-200 hover:border-gray-300 text-sm"
                        disabled={isLoading}
                      >
                        <option value="">No team assigned</option>
                        {teams.map((team) => (
                          <option key={team.id} value={team.id}>
                            {team.name}{team.is_default ? ' (Default)' : ''}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1.5 text-xs text-gray-400">All members of the assigned team will have access to this project.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer with navigation */}
          <div className="px-6 py-4 bg-gray-50/80 border-t border-gray-100 flex items-center gap-3">
            {currentPage > 1 ? (
              <button
                type="button"
                onClick={handleBack}
                disabled={isLoading}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-gray-600 font-medium
                  hover:bg-gray-100 transition-all duration-200 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
            ) : (
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2.5 rounded-xl text-gray-600 font-medium
                  hover:bg-gray-100 transition-all duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
            )}
            <div className="flex-1" />
            {currentPage < TOTAL_PAGES ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={isLoading}
                className="flex items-center gap-1.5 px-5 py-2.5 bg-[#C3D74F] text-[#3d4a0e] rounded-xl font-medium
                  hover:bg-[#d4e66a] active:scale-[0.98] transition-all duration-200 disabled:opacity-50
                  shadow-sm shadow-[#C3D74F]/25 hover:shadow-md hover:shadow-[#C3D74F]/35"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isLoading || !name.trim()}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#C3D74F] text-[#3d4a0e] rounded-xl font-medium
                  hover:bg-[#d4e66a] active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
                  shadow-sm shadow-[#C3D74F]/25 hover:shadow-md hover:shadow-[#C3D74F]/35"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProjectModal;
